// src/components/admin/RoleManagement.jsx
import React, { useState } from 'react';
import toast from 'react-hot-toast';
import { Pencil, Plus, Trash2 } from 'lucide-react';
import { useRoles } from '../../hooks/useRoles';
import { useUsers } from '../../hooks/useUsers';
import { useTools } from '../../hooks/useTools';
import RoleForm from './RoleForm';
import {
  Button,
  ErrorState,
  IconButton,
  Modal,
  PageHeader,
  Pill,
  RowActions,
  Spinner,
} from '../common/ui';

const Stat = ({ value, label }) => (
  <div>
    <p className="tnum font-display text-lg font-semibold leading-none">{value}</p>
    <p className="mt-1 text-[10.5px] font-medium uppercase tracking-[0.07em] text-ink-3">{label}</p>
  </div>
);

const RoleManagement = () => {
  const { roles, isLoading, error, refetch, createRole, updateRole, deleteRole } = useRoles();
  const { users } = useUsers();
  const { tools } = useTools(true);

  const [editing, setEditing] = useState(null);
  const [isSaving, setIsSaving] = useState(false);

  const peopleIn = (roleId) => users.filter((u) => (u.role?._id || u.role) === roleId).length;
  const toolsFor = (roleId) =>
    tools.filter((t) => (t.allowedRoles || []).some((r) => (r._id || r) === roleId)).length;

  const handleSubmit = async (data) => {
    setIsSaving(true);
    try {
      if (editing._id) {
        await updateRole({ roleId: editing._id, roleData: data });
        toast.success(`Role "${data.name}" updated`);
      } else {
        await createRole(data);
        toast.success(`Role "${data.name}" created`);
      }
      setEditing(null);
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (role) => {
    const people = peopleIn(role._id);
    const warning = people
      ? `\n\n${people} ${people === 1 ? 'person is' : 'people are'} on this role and will lose access to its tools.`
      : '';

    if (!window.confirm(`Delete the "${role.name}" role?${warning}`)) return;

    try {
      await deleteRole(role._id);
      toast.success(`Role "${role.name}" deleted`);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Could not delete that role');
    }
  };

  return (
    <div>
      <PageHeader
        title="Roles"
        subtitle="A role is a bundle of tools. Give someone a role and they get everything in it."
      >
        <Button variant="primary" onClick={() => setEditing({})}>
          <Plus className="h-4 w-4" />
          New role
        </Button>
      </PageHeader>

      {isLoading ? (
        <Spinner label="Loading roles…" />
      ) : error ? (
        <ErrorState title="Couldn't load roles" onRetry={refetch}>
          {error.message}
        </ErrorState>
      ) : (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3">
          {roles.map((role) => (
            <div
              key={role._id}
              className="flex flex-col gap-2.5 rounded-card border border-line bg-surface p-4"
            >
              <div className="flex items-start justify-between gap-3">
                <h3 className="text-[15.5px] font-semibold capitalize">{role.name}</h3>
                <Pill tone={role.isDefault ? 'accent' : 'neutral'}>
                  {role.isDefault ? 'Built in' : 'Custom'}
                </Pill>
              </div>

              <p className="flex-1 text-[13.5px] leading-relaxed text-ink-2">{role.description}</p>

              <div className="flex items-end gap-6 border-t border-line pt-3">
                <Stat value={peopleIn(role._id)} label="People" />
                <Stat value={toolsFor(role._id)} label="Tools" />
                <div className="ml-auto">
                  <RowActions>
                    <IconButton label={`Edit ${role.name}`} onClick={() => setEditing(role)}>
                      <Pencil className="h-4 w-4" />
                    </IconButton>
                    <IconButton
                      label={
                        role.isDefault
                          ? 'Built-in roles can’t be deleted'
                          : `Delete ${role.name}`
                      }
                      tone="danger"
                      disabled={role.isDefault}
                      onClick={() => handleDelete(role)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </IconButton>
                  </RowActions>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {editing && (
        <Modal
          title={editing._id ? `Edit the ${editing.name} role` : 'New role'}
          description={
            editing._id
              ? 'Tools are assigned to roles over in Admin → Tools.'
              : 'Create it here, then tick it on the tools it should unlock.'
          }
          onClose={() => setEditing(null)}
        >
          <RoleForm
            role={editing}
            isSaving={isSaving}
            onSubmit={handleSubmit}
            onCancel={() => setEditing(null)}
          />
        </Modal>
      )}
    </div>
  );
};

export default RoleManagement;
